import type { ApolloError } from '@apollo/client';
import { OrderType } from '@exam-management/graphql';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import {
  type DataGridProps,
  type GridColDef,
  type GridSortingInitialState,
  type GridSortModel,
  ChipGroup,
  DataGrid,
  ErrorBox,
  Label,
  NoRowsOverlay,
} from '@shared/components';
import { dayjs, formatDateTime, unionArray } from '@shared/utils';
import type { ChangeEvent } from 'react';
import { useController } from 'react-hook-form';

import { USER_COLOR_STATUS, USER_STATUS_TEXT } from '../../constants';
import { UserAvatarAndEmail } from '../../containers';
import { useUserList } from '../../provider/use-user-list';
import { type UserRow, UserStatus, type UserListDefColumn } from '../../types';

function getFieldName(field: string) {
  if (field === 'profile') return 'firstname';
  return field;
}

const columns: UserListDefColumn[] = [
  {
    field: 'profile',
    headerName: 'Name',
    flex: 1,
    renderCell: (params) => (
      <UserAvatarAndEmail
        firstName={params.row.profile.firstName}
        lastName={params.row.profile.lastName}
        email={params.row.profile.email}
        profilePic={params.row.profile.profilePic}
      />
    ),
  },
  {
    field: 'appRoles',
    headerName: 'Role',
    sortable: false,
    width: 240,
    renderCell: (params) => {
      if (!params.row.appRoles) return null;
      return <ChipGroup labels={params.row.appRoles} limit={2} />;
    },
  },
  {
    field: 'lastLoginAt',
    headerName: 'Last Login',
    type: 'string',
    width: 160,
    valueFormatter: (params) => {
      return `${params.value ? formatDateTime(params.value) : '-'}`;
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 160,
    renderCell: (params) => {
      if (!params.row.status) return null;

      let status = params.row.status;
      if (
        status === UserStatus.INVITATION_PENDING &&
        dayjs(params.row.invitationExpireAt).isBefore(dayjs())
      ) {
        status = UserStatus.INVITATION_EXPIRED;
      }
      return (
        <Label
          sx={{ px: (theme) => theme.spacing(3) }}
          color={USER_COLOR_STATUS[status]}
          variant='outlined'
        >
          {USER_STATUS_TEXT[status]}
        </Label>
      );
    },
  },
];

export interface UserDataGridProps extends Omit<DataGridProps, 'columns'> {
  isLoading: boolean;
  totalCount: number;
  moreColumns?: GridColDef[];
  name?: string;
  error?: ApolloError;
}

const dataGridBaseProps: Omit<DataGridProps, 'rows' | 'columns'> = {
  disableColumnMenu: true,
  getRowId: (row) => row.userId,
  slots: {
    noRowsOverlay: NoRowsOverlay,
  },
  sx: (theme) => ({
    backgroundColor: theme.palette.common.white,
    '--DataGrid-overlayHeight': '412px',
    '& .MuiDataGrid-cell': {
      padding: theme.spacing(3),
    },
    '& .MuiDataGrid-row': {
      borderBottom: '2px solid',
      borderBottomColor: theme.palette.grey[300],
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '2px solid',
      borderBottomColor: theme.palette.grey[300],
    },
  }),
};

export function UserDataGrid(props: Readonly<UserDataGridProps>) {
  const { moreColumns = [], isLoading, totalCount, error, rows, ...restProps } = props;

  const shouldHideFooter = isLoading || totalCount === 0;

  const {
    dataGridParams: { page, pageSize, field, sort },
    setDataGridParams,
  } = useUserList();

  const handlePageChange = (page: number) => {
    setDataGridParams({ page });
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setDataGridParams({ pageSize: parseInt(event.target.value), page: 0 });
  };

  const handleSortModelChange = (sorting: GridSortModel) => {
    const field = sorting[0]?.field ?? 'profile';
    const sort = (sorting[0]?.sort?.toUpperCase() ?? OrderType.Asc) as OrderType;
    setDataGridParams({ field: getFieldName(field), sort });
  };

  const dataGridProps: DataGridProps = {
    ...dataGridBaseProps,
    loading: isLoading,
    rows: rows,
    columns: columns.concat(moreColumns),
    hideFooter: shouldHideFooter,
    slotProps: {
      footer: {
        total: totalCount,
        pagination: {
          page: page,
          count: totalCount,
          rowsPerPage: pageSize,
          onPageChange: (_event, page) => {
            handlePageChange(page);
          },
          onRowsPerPageChange: handleRowsPerPageChange,
        },
      },
    },
    initialState: {
      sorting: {
        sortModel: field
          ? [{ field: field === 'firstname' ? 'profile' : field, sort: sort.toLowerCase() }]
          : undefined,
      } as GridSortingInitialState,
    },
    onSortModelChange: handleSortModelChange,
  };

  if (error) {
    return <ErrorBox />;
  }

  return <DataGrid {...dataGridProps} {...restProps} />;
}

export function UserDataGridForm(props: Readonly<UserDataGridProps>) {
  const {
    field: { onChange, value: selectedUserIds },
    formState: { isSubmitting },
  } = useController({ ...props, name: props.name ?? 'userIds' });

  const { onSelectUser } = useUserList();

  const handleRowSelect = (rowSelectionModel: GridRowSelectionModel) => {
    //TODO: Improve performance
    onSelectUser?.((prev: UserRow[]) => {
      const newSelectedUsers: UserRow[] = props.rows.filter((row) =>
        rowSelectionModel.includes(row.userId),
      );
      const oldSelectedUsers = prev.filter((row) => rowSelectionModel.includes(row.userId));
      return unionArray<UserRow>(oldSelectedUsers, newSelectedUsers, 'userId');
    });
    onChange(rowSelectionModel, { shouldValidate: true });
  };

  const dataGridWithCheckboxProps: Omit<DataGridProps, 'rows' | 'columns'> = {
    checkboxSelection: true,
    keepNonExistentRowsSelected: true,
    onCellClick: (cellParams, event, details) => {
      if (cellParams.field === '__check__') return;
      props?.onCellClick?.(cellParams, event, details);
    },
    loading: isSubmitting || props.isLoading,
    rowSelectionModel: selectedUserIds,
    onRowSelectionModelChange: handleRowSelect,
  };

  return <UserDataGrid {...dataGridWithCheckboxProps} {...props} />;
}
